/**
 * Sidebar Store - Zustand state management for the 3-panel sidebar navigation
 *
 * Manages:
 * - Main sidebar state: expanded (232px), collapsed (50px), hidden (0px)
 * - Icon rail visibility (multi-location quick-switcher)
 * - Section open/closed states for location and network menus
 * - Focus mode (hides everything)
 * - LocalStorage persistence
 */

import { create } from 'zustand'
import { devtools } from 'zustand/middleware'

export type SidebarMainState = 'expanded' | 'collapsed' | 'hidden'

interface SidebarState {
  /** Main sidebar state: expanded (232px), collapsed (50px), hidden (0px) */
  mainState: SidebarMainState
  /** Icon rail visibility (only relevant when hasMultipleLocations) */
  railVisible: boolean
  /** Which sections are open, keyed by section id */
  openSections: Record<string, boolean>

  // Actions
  setMainState: (state: SidebarMainState) => void
  cycleSidebar: () => void
  toggleRail: () => void
  setRailVisible: (visible: boolean) => void
  toggleSection: (sectionId: string) => void
  openSection: (sectionId: string) => void
  enterFocusMode: () => void
  exitFocusMode: () => void
}

const STORAGE_KEY = 'planmax_sidebar'

function loadPersistedState(): Partial<Pick<SidebarState, 'mainState' | 'railVisible'>> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw)
      return {
        mainState: parsed.mainState ?? 'expanded',
        railVisible: parsed.railVisible ?? false,
      }
    }
  } catch {
    // ignore
  }
  return {}
}

function persistState(state: Pick<SidebarState, 'mainState' | 'railVisible'>) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      mainState: state.mainState,
      railVisible: state.railVisible,
    }))
  } catch {
    // ignore
  }
}

const CYCLE_ORDER: SidebarMainState[] = ['expanded', 'collapsed', 'hidden']

// State saved before entering focus mode, for restoration
let preFocusState: { mainState: SidebarMainState; railVisible: boolean } | null = null

export const useSidebarStore = create<SidebarState>()(
  devtools(
    (set, get) => {
      const persisted = loadPersistedState()

      return {
        mainState: persisted.mainState ?? 'expanded',
        railVisible: persisted.railVisible ?? false,
        openSections: {},

        setMainState: (mainState) => {
          set({ mainState }, false, 'setMainState')
          persistState({ mainState, railVisible: get().railVisible })
        },

        cycleSidebar: () => {
          const current = get().mainState
          const idx = CYCLE_ORDER.indexOf(current)
          const next = CYCLE_ORDER[(idx + 1) % CYCLE_ORDER.length]
          set({ mainState: next }, false, 'cycleSidebar')
          persistState({ mainState: next, railVisible: get().railVisible })
        },

        toggleRail: () => {
          const next = !get().railVisible
          set({ railVisible: next }, false, 'toggleRail')
          persistState({ mainState: get().mainState, railVisible: next })
        },

        setRailVisible: (visible) => {
          set({ railVisible: visible }, false, 'setRailVisible')
          persistState({ mainState: get().mainState, railVisible: visible })
        },

        toggleSection: (sectionId) => {
          set(
            (state) => ({
              openSections: {
                ...state.openSections,
                [sectionId]: !state.openSections[sectionId],
              },
            }),
            false,
            'toggleSection'
          )
        },

        openSection: (sectionId) => {
          set(
            (state) => ({
              openSections: {
                ...state.openSections,
                [sectionId]: true,
              },
            }),
            false,
            'openSection'
          )
        },

        enterFocusMode: () => {
          const { mainState, railVisible } = get()
          preFocusState = { mainState, railVisible }
          set({ mainState: 'hidden', railVisible: false }, false, 'enterFocusMode')
          persistState({ mainState: 'hidden', railVisible: false })
        },

        exitFocusMode: () => {
          if (preFocusState) {
            set(
              { mainState: preFocusState.mainState, railVisible: preFocusState.railVisible },
              false,
              'exitFocusMode'
            )
            persistState(preFocusState)
            preFocusState = null
          } else {
            set({ mainState: 'expanded' }, false, 'exitFocusMode')
            persistState({ mainState: 'expanded', railVisible: get().railVisible })
          }
        },
      }
    },
    { name: 'SidebarStore' }
  )
)
